import React from 'react';
import {action, computed, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import {engravedPlateExtendedStore, EPLine, Font, FONT_FAMILY_MAP, FONT_OPTIONS, FONT_SIZES, loadEPFont, Size} from './engraved-plate-extended.store';
import s from '../../../s';
import {DefaultPCS, EPBorderType, EPMethod, EPSizeType, EP_DEFAULT_FIXED_SIZE, isEPAdmin, isEPCustomLike} from '../../../rendering/render.def';
import {isNullOrWhiteSpace, Portal} from '../../../../util';
import ModalSimple from '../../../../global/modal-simple';
import InfoIcon from '../../shared/info-icon';
import {EPEditor} from '../ep-editor';
import {EPAdminSource} from '../ep-admin-source';
import {EPTemplate} from '../ep-template/ep-template';
import {applyTemplate, setTemplateLine, TemplateLineRow, templateLineRows, updateTemplateContent} from '../ep-template/ep-template-apply';

// NOTE FOR C# RENDERER

// Example custom_text_6 string (10 lines possible):
// 0:1-L-0-0:2-S-1-0:3-S-0-0
// Interpretation rules for renderer:
//  - Split by ':' to get per-line tokens
//  - First token is always fontIndex (0 or 1)
//  - All other line tokens => <lineNumber>-<lineHeight>-<bold>-<italic>
//  - LineHeight: S -> 0.25, M -> 0.375, L -> 0.5
//  - The total plate vertical height should be calculated as sum(lineHeights) + rail (0.25" default).

@observer
export default class EngravedPlateExtended extends React.Component {
    @observable lines: EPLine[] = [];
    @observable collapsed: boolean = true;
    @observable font: Font = Font.Roboto;
    private _autoAddThrottled = false;

    constructor(props: any) {
        super(props);
        makeObservable(this);
        this.init();
    }

    componentDidMount() {
        // Auto-expand the panel when flagged by the mode switch, or in admin template mode
        // (ep_type=6 / EPMethod.Admin), so the source editor + preview show without a click.
        if (s.ui.ep_mode_expanded || isEPAdmin(s.ps.EPMethodID)) {
            s.ui.ep_mode_expanded = false;
            const el = document.getElementById('ep-inner');
            if (el) el.classList.add('show');
        }
        this.syncEpColorSelect();
    }

    componentDidUpdate() {
        this.syncEpColorSelect();
    }

    syncEpColorSelect = () => {
        const select = document.getElementById('ep-color-select') as HTMLSelectElement;
        if (!select) return;
        const target = String(s.ps.ep_id_view);
        const options = Array.from(select.options).map(o => o.value);
        if (options.includes(target)) {
            select.value = target;
        } else if (options.length > 0) {
            select.value = options[0];
            s.ps.ep_id_view = parseInt(options[0], 10);
            if (s.ps.ep_id > 0) {
                s.ps.ep_id = s.ps.ep_id_view;
            }
        }
    };

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    @action
    getDefaultLine = (value?: string) => {
        return {text: value || '', lineHeight: Size['24pt'], size: Size['24pt'], bold: false, italic: false};
    };

    @action
    init = () => {
        // Template-mode line values are positional (ep{N} fills the [line{N}]/[[ep{N}]] token),
        // but the lines/pushToPCS pipeline below compacts non-empty values downward, which would
        // shift saved values onto the wrong template lines on reload. Snapshot to restore after.
        const savedTemplateLineValues = s.ps.EPMethodID === EPMethod.Template ? Array.from({length: 10}, (_, i) => s.ps[`ep${i + 1}`] || '') : null;

        // initialize lines from existing s.ps.ep1..ep10 if present so we preserve old behavior
        for (let i = 1; i <= 10; i++) {
            const val = s.ps[`ep${i}`] || '';

            if (val) {
                this.lines.push(this.getDefaultLine(val));
            }
        }

        // If no existing lines, default to the number of lines the product comes with
        // (s.prod.EngravedPlateLines), falling back to 4 when not specified. Clamp to the
        // 1–10 range the editor supports.
        if (this.lines.length === 0) {
            DefaultPCS.DefaultEPWidth += 0.5;
            const defaultLineCount = Math.min(10, Math.max(1, s.prod.EngravedPlateLines || 4));
            for (let i = 0; i < defaultLineCount; i++) {
                this.lines.push(this.getDefaultLine());
            }
        }

        // push initial state into s.pcs so frame rendering can read it
        this.pushToPCS();
        if (savedTemplateLineValues) {
            for (let i = 0; i < 10; i++) {
                s.ps[`ep${i + 1}`] = savedTemplateLineValues[i];
            }
        }
        if (s.pcs.EPMethodID === EPMethod.Template) {
            updateTemplateContent();
            // updateTemplateContent only mutates s.ps.EPContent, but the canvas paints from
            // s.pcs.EPContent — which is copied from s.ps.EPContent only inside prerender_set_pcs
            // during a pulse(). pushToPCS() above already pulsed (with empty EPContent), so fire
            // another render now that the template HTML exists; otherwise the plate paints blank
            // until the user toggles remove/add. Mirrors onTemplateLineChange/applyTemplate.
            s.ui.doRender = true;
            s.pulse();
        }
        this.getFontFamily();
        loadEPFont(this.font);
        engravedPlateExtendedStore.lines = this.lines;
    };

    @action
    toggleCollapse = () => {
        this.collapsed = !this.collapsed;
    };

    @action
    switchToSimple = () => {
        s.ui.ep_mode_expanded = true;
        s.prod.EPMethodID = EPMethod.Old;
        s.ps.EPMethodID = EPMethod.Old;
        s.prod.EPBorderType = EPBorderType.Inset;
        s.ps.EPBorderType = EPBorderType.Inset;
        s.prod.EPSizeType = EPSizeType.Default;
        s.ps.EPSizeType = EPSizeType.Default;
        s.prod.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
        s.prod.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
        s.ps.EPWidth = EP_DEFAULT_FIXED_SIZE.width;
        s.ps.EPHeight = EP_DEFAULT_FIXED_SIZE.height;
        DefaultPCS.DefaultEPWidth = EP_DEFAULT_FIXED_SIZE.width;
        DefaultPCS.DefaultEPHeight = EP_DEFAULT_FIXED_SIZE.height;
        s.ps.EPContent = '';
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    };

    // helper to compute custom_text_6 string required by backend/C#
    // first token is always fontIndex (0 or 1)
    // format per-line token: <lineNumber>-<LineHeight>-<Bold>-<Italic>
    // tokens joined by ':'
    buildCustomText6(): string {
        const lineTokens = this.lines.map((ln, idx) => {
            const lineNumber = idx + 1;
            const bold = ln.bold ? 1 : 0;
            const italic = ln.italic ? 1 : 0;

            return `${lineNumber}-${ln.lineHeight}-${bold}-${italic}`;
        });

        return `${this.font}:${lineTokens.join(':')}`;
    }

    // push all EP related pieces to s.pcs so renderer + pricing can react
    @action
    pushToPCS() {
        for (let i = 0; i < 10; i++) {
            const key = `ep${i + 1}`;

            s.ps[key] = this.lines[i] ? this.lines[i].text : '';
        }

        // set custom_text_6 to the encoded configuration string
        s.ps.custom_text_6 = this.buildCustomText6();

        // set dynamic EP height
        DefaultPCS.DefaultEPHeight = this.getEPHeight();

        // trigger UI/render/pricing updates
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }

    // Auto-add plate to frame on first typing if not already added (throttled)
    autoAddIfNeeded = () => {
        if (s.ps.ep_id > 0 || this._autoAddThrottled) return;
        this._autoAddThrottled = true;
        this.ep_add();
        setTimeout(() => {
            this._autoAddThrottled = false;
        }, 1000);
    };

    // called whenever a line's text changes
    @action
    onLineTextChange = (index: number, value: string) => {
        if (!this.lines[index]) return;

        this.lines[index].text = value;
        this.autoAddIfNeeded();
        this.pushToPCS();
    };

    // The template steps (apply, dropdown and line defaults, HTML resolution) live in
    // ep-template/ep-template-apply.ts, shared with the inst-plate module.
    @action
    onTemplateLineChange = (lineNumber: number, value: string) => {
        this.autoAddIfNeeded();
        setTemplateLine(lineNumber, value);
    };

    @action
    onTemplateSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = parseInt(e.target.value, 10);
        const tpl = (s.prod.EPTemplates || []).find(t => t.id === id);

        if (tpl) {
            applyTemplate(tpl);
        }
    };

    // add another line (max 10)
    @action
    addLine = () => {
        if (this.lines.length >= 10) return;

        this.lines.push(this.getDefaultLine());
        this.pushToPCS();
    };

    // remove a line at index
    @action
    removeLine = (index: number) => {
        if (index < 0 || index >= this.lines.length) return;

        this.lines.splice(index, 1);
        // ensure at least one line exists
        if (this.lines.length === 0) {
            this.lines.push();
        }

        this.pushToPCS();
    };

    @action
    changeSize = (index: number, size: Size) => {
        const ln = this.lines[index];

        if (!ln) return;

        ln.size = size;
        ln.lineHeight = size;

        this.pushToPCS();
    };

    @action
    toggleBold = (index: number) => {
        const ln = this.lines[index];

        if (!ln) return;

        ln.bold = !ln.bold;
        this.pushToPCS();
    };

    @action
    toggleItalic = (index: number) => {
        const ln = this.lines[index];

        if (!ln) return;

        ln.italic = !ln.italic;
        this.pushToPCS();
    };

    @action
    onFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.font = +e.target.value as Font;
        this.getFontFamily();
        loadEPFont(this.font);
        this.pushToPCS();
    };

    @action
    ep_add = () => {
        // set a default ep_id if none
        if (!s.ps.ep_id || s.ps.ep_id <= 0) {
            s.ps.ep_id = s.ps.ep_id_view || 37;
        }

        s.ui.doRender = true;
        s.ps.lino_base_name = '';
        s.ps.lino_id = -1;
        s.ui.update_pricing = true;
        s.pulse();
    };

    @action
    ep_remove = () => {
        if (s.ps.ep_id > 0) {
            s.ps.ep_id = 0;
            s.ui.update_pricing = true;
            s.ui.doRender = true;
            s.pulse();
        }
    };

    getFontFamily = () => {
        engravedPlateExtendedStore.fontFamily = FONT_FAMILY_MAP[this.font] ?? FONT_FAMILY_MAP[Font.Roboto];
    };

    getEPHeight = () => {
        return this.lines.reduce((acc, ln) => {
            if (!ln.text || isNullOrWhiteSpace(ln.text)) {
                return acc;
            }

            return acc + ln.lineHeight / s.pcs.DPI;
        }, 0.43);
    };

    @computed get visibleTemplateLines(): TemplateLineRow[] {
        return templateLineRows();
    }

    render() {
        if (!s.ui.show_cust || !s.prod.EngravedPlateStatusID || s.prod.EngravedPlateStatusID < 1) {
            return null;
        }

        return (
            <div
                className="gray-box ep"
                style={{padding: 0}}
            >
                <ModalSimple
                    id="modal-sample-ep"
                    title="Personalized Plate Information"
                    maxWidth="600px"
                >
                    <div>
                        {s.ps.EPMethodID <= EPMethod.Extended && (
                            <ul>
                                {s.prod.EngravedPlateStatusID === 0 && <li>This product does not allow an personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 1 && <li>This product requires a gold personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 2 && <li>This product requires a silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 3 && <li>This product allows a gold personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 4 && <li>This product allows a silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 5 && <li>This product allows a gold or silver personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 6 && <li>This product requires a black personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 7 && <li>This product allows a black personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 8 && <li>This product requires a white personalized plate</li>}
                                {s.prod.EngravedPlateStatusID === 9 && <li>This product allows a white personalized plate</li>}
                                <li>Enter up to 30 characters per line</li>
                                <li>You may have up to 10 lines centered on the personalized plate</li>
                                <li>Dimensions: Depends on how many lines entered (plate image may be stretched vertically if needed)</li>
                                <li>If you do not provide text, a plate will not be included with your order</li>
                            </ul>
                        )}

                        {s.ps.EPMethodID > EPMethod.Extended && (
                            <ul>
                                <li>This product allows a gold or silver personalized plate</li>
                                <li>Choose one or multiple fonts and font sizes to create your custom plate</li>
                                <li>Dimensions: (dynamic)</li>
                                <li>Depending on the plate size, you can have multiple lines of text. If you add too many characters to a line or too many lines for that plate size, you will be notified</li>
                                <li>If you do not provide text, a plate will not be included with your order</li>
                            </ul>
                        )}

                        <div>
                            <b>Example personalized plate:</b>
                        </div>
                        <img
                            src={`https://${Portal?.CDNDomain}/images/products/full/80049pid25402.png`}
                            alt="Example personalized plate"
                            aria-label="Example personalized plate"
                        />
                    </div>
                </ModalSimple>

                <div className={this.canCollapse && s.ps.is_caf ? '' : ''}>
                    <div style={{paddingLeft: 12, paddingRight: 12, paddingTop: 0, paddingBottom: 0, position: 'relative'}}>
                        <div
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-block toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>Personalized Plate: </span>
                                <span>{s.ps.ep_id <= 0 ? '' : s.ps.ep_id === 37 ? 'Gold' : s.ps.ep_id === 39 ? 'Silver' : s.ps.ep_id === 1099 ? 'Black' : s.ps.ep_id === 33146 ? 'White' : ''}</span>
                            </div>
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-block d-xl-none toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        Personalized Plate
                                    </div>
                                    <div className="cust-flex2">{s.ps.ep_id <= 0 ? '' : s.ps.ep_id === 37 ? 'Gold' : s.ps.ep_id === 39 ? 'Silver' : s.ps.ep_id === 1099 ? 'Black' : s.ps.ep_id === 33146 ? 'White' : ''}</div>
                                </div>
                                <div>
                                    {s.ps.ep_id > 0 && (
                                        <img
                                            className="th-h"
                                            src={
                                                s.ps.ep_id === 37
                                                    ? `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_gold_original_engravedplate.png`
                                                    : s.ps.ep_id === 1099
                                                      ? `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_black_original_engravedplate.png`
                                                      : s.ps.ep_id === 33146
                                                        ? `https://${Portal?.CDNDomain}/images/cached/general/ep_white_original_engravedplate.png`
                                                        : `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_silver_original_engravedplate.png`
                                            }
                                            alt="Personalized Plate Thumbnail"
                                            style={{height: 48, width: 'auto'}}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="ep-inner"
                        className={this.canCollapse ? 'collapse' : ''}
                        style={{padding: '0 12px'}}
                    >
                        <div>
                            <div
                                className={`ep-container ${(() => {
                                    const c = s.ps.ep_id > 0 ? s.ps.ep_id : s.ps.ep_id_view;

                                    return c === 37 ? 'gold' : c === 1099 ? 'black' : c === 33146 ? 'white' : 'silver';
                                })()}`}
                                style={{textAlign: 'left', width: '100%'}}
                            >
                                {isEPAdmin(s.ps.EPMethodID) ? (
                                    <EPAdminSource />
                                ) : isEPCustomLike(s.ps.EPMethodID) || s.pcs.EPMethodID === EPMethod.SingleDoc ? (
                                    <div onInput={() => this.autoAddIfNeeded()}>
                                        <EPEditor />
                                    </div>
                                ) : s.pcs.EPMethodID === EPMethod.Template ? (
                                    <EPTemplate
                                        visibleTemplateLines={this.visibleTemplateLines}
                                        onTemplateLineChange={this.onTemplateLineChange}
                                        templates={s.prod.EPTemplates || []}
                                        selectedTemplateID={s.ps.EPTemplateID}
                                        onTemplateSelectChange={this.onTemplateSelectChange}
                                    />
                                ) : (
                                    <>
                                        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 8}}>
                                            <label
                                                htmlFor="ep-font-select"
                                                style={{marginRight: 8, alignSelf: 'center'}}
                                            >
                                                Font:
                                            </label>
                                            <select
                                                id="ep-font-select"
                                                name="ep-font-select"
                                                aria-label="Select personalized plate font"
                                                value={this.font}
                                                onChange={this.onFontChange}
                                            >
                                                {FONT_OPTIONS.map(f => (
                                                    <option
                                                        key={f.value}
                                                        value={f.value}
                                                    >
                                                        {f.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {this.lines.map((ln, idx) => (
                                            <div
                                                key={idx}
                                                style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6}}
                                            >
                                                <div style={{display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center'}}>
                                                    <label htmlFor={`ep-font-size-${idx}`}>Font Size:</label>
                                                    <select
                                                        id={`ep-font-size-${idx}`}
                                                        name={`ep-font-size-${idx}`}
                                                        className="ep-font-size"
                                                        aria-label={`Select font size for line ${idx + 1}`}
                                                        value={ln.size}
                                                        onChange={e => this.changeSize(idx, +e.target.value as Size)}
                                                    >
                                                        {FONT_SIZES.map(f => (
                                                            <option
                                                                key={f.value}
                                                                value={f.value}
                                                            >
                                                                {f.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <button
                                                        className="toggle-btn"
                                                        type="button"
                                                        aria-label={`Toggle bold for line ${idx + 1}`}
                                                        onClick={() => this.toggleBold(idx)}
                                                        style={{fontWeight: ln.bold ? '700' : '400'}}
                                                    >
                                                        B
                                                    </button>

                                                    <button
                                                        className="toggle-btn"
                                                        type="button"
                                                        aria-label={`Toggle italic for line ${idx + 1}`}
                                                        onClick={() => this.toggleItalic(idx)}
                                                        style={{
                                                            fontStyle: ln.italic ? 'italic' : 'normal',
                                                            fontWeight: ln.italic ? '700' : '400',
                                                        }}
                                                    >
                                                        I
                                                    </button>

                                                    <button
                                                        className="ep-btn"
                                                        type="button"
                                                        aria-label={`Remove line ${idx + 1}`}
                                                        onClick={() => this.removeLine(idx)}
                                                        disabled={this.lines.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                <input
                                                    id={`ep-${idx + 1}`}
                                                    type="text"
                                                    className="ep-text"
                                                    maxLength={30}
                                                    aria-label={`Line ${idx + 1} of the personalized plate`}
                                                    placeholder={`Line ${idx + 1}`}
                                                    autoComplete="off"
                                                    value={ln.text}
                                                    onChange={e => this.onLineTextChange(idx, e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        fontWeight: ln.bold ? '700' : '400',
                                                        fontSize: `${ln.size}px`,
                                                        lineHeight: `${ln.lineHeight}px`,
                                                        fontStyle: ln.italic ? 'italic' : 'normal',
                                                        fontFamily: engravedPlateExtendedStore.fontFamily,
                                                    }}
                                                />
                                            </div>
                                        ))}

                                        <div style={{marginTop: 8, display: 'flex', justifyContent: 'flex-end'}}>
                                            <button
                                                className="ep-btn"
                                                type="button"
                                                onClick={this.addLine}
                                                disabled={this.lines.length === 10}
                                                aria-label="Add more personalized plate lines"
                                            >
                                                Add line
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="ep-option">
                                {s.prod.IsCAF && s.pcs.EPMethodID === EPMethod.Custom && (
                                    <div className="ep-mode-switch">
                                        <button
                                            type="button"
                                            aria-label="Switch to simple personalized plate editor"
                                            onClick={this.switchToSimple}
                                        >
                                            Simple Mode
                                        </button>
                                    </div>
                                )}
                                {s.prod.EngravedPlateStatusID === 5 && s.ps.ins_gold_allowed && s.ps.ins_silver_allowed && s.ps.mat2_id !== 385 && (
                                    <div className="ep-color">
                                        <select
                                            id="ep-color-select"
                                            name="ep-color-select"
                                            onChange={e => {
                                                s.ps.ep_id_view = parseInt(e.target.value, 10);
                                                if (s.ps.ep_id > 0) {
                                                    s.ps.ep_id = s.ps.ep_id_view;
                                                    s.ui.doRender = true;
                                                    s.ui.update_pricing = true;
                                                    s.pulse();
                                                }
                                            }}
                                            aria-label="Select personalized plate color"
                                        >
                                            {s.ps.ins_gold_allowed && <option value="37">Gold</option>}
                                            {s.ps.ins_silver_allowed && s.ps.mat2_id !== 385 && <option value="39">Silver</option>}
                                        </select>
                                    </div>
                                )}

                                {s.prod.EngravedPlateStatusID !== 1 && s.prod.EngravedPlateStatusID !== 2 && s.prod.EngravedPlateStatusID !== 6 && s.prod.EngravedPlateStatusID !== 8 && (
                                    <div className="ep-toggle">
                                        {(!s.ps.ep_id || s.ps.ep_id <= 0) && (
                                            <button
                                                type="button"
                                                aria-label="Add personalized plate to product"
                                                onClick={this.ep_add}
                                            >
                                                Add Plate to Frame
                                            </button>
                                        )}
                                        {s.ps.ep_id > 0 && (
                                            <button
                                                type="button"
                                                aria-label="Remove personalized plate from product"
                                                onClick={this.ep_remove}
                                            >
                                                Remove Plate from Frame
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="ep-link mb-1">
                                View information about personalized plates
                                <InfoIcon
                                    id="modal-sample-ep"
                                    ariaLabel="View more information about personalized plates"
                                    useMargin={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
